@if (errorMessage) {
  <tidy-error-message [text]="errorMessage" />
}

@if (windowService.isDesktopRes) {
  <tidy-desktop-header>
    <div class="wrappable-container">
      <tidy-text [header]="'Upgrades'" />
      <form [formGroup]="form" novalidate action="#">
        <tidy-search-input
          formControlName="search"
          [placeholder]="'Search'"
          [icon]="'assets/img/search-header.svg'"
          class="header-search" />
      </form>
      <form [formGroup]="form" novalidate action="#">
        <tidy-header-select
          [items]="addressFilter"
          [form]="form.get('selectedAddress')"
          formControlName="selectedAddress"
          (optionChange)="onFilterValueChange()"
          [icon]="'assets/svg/home.svg'" />
      </form>
      <form [formGroup]="form" novalidate action="#">
        <tidy-header-select
          [items]="sortOptions"
          [form]="form.get('sortKey')"
          formControlName="sortKey"
          [icon]="'assets/svg/swap-vertical-outline.svg'" />
      </form>
      <tidy-small-button
        [text]="'Add Upgrade'"
        [image]="'assets/img/add-button-white.svg'"
        (buttonClick)="goToAddUpgradePage()" />
    </div>
    <div class="non-wrappable-right-aligned-container">
      <automations-preview-button
        [tag]="'upgrades'" />
    </div>
  </tidy-desktop-header>
}

<!-- The skeletons float in on landing (sk-section entrance animation); when
     data lands the real content replaces every skeleton in place instantly,
     with no animation. The placeholders copy the real layout metrics
     (tidy-table's own skeleton / upgrade-card rows / mobile control pills) so
     the swap happens without the page shifting. -->
@if (!windowService.isDesktopRes) {
  @if (isDataLoaded) {
    <div style="margin-bottom: 10px;">
      <form [formGroup]="form" novalidate action="#">
        <tidy-search-input
          formControlName="search"
          [placeholder]="'Search'"
          [icon]="'assets/img/search-header.svg'"
          class="header-search" />
      </form>
    </div>
    <div class="custom-desktop-header" style="margin-bottom: 10px;">
      <form [formGroup]="form" novalidate action="#" class="form-filters" style="gap: 8px;">
        <tidy-header-select
          [items]="addressFilter"
          [form]="form.get('selectedAddress')"
          formControlName="selectedAddress"
          (optionChange)="onFilterValueChange()"
          [icon]="'assets/svg/home.svg'" />
        <tidy-header-select
          [items]="sortOptions"
          [form]="form.get('sortKey')"
          formControlName="sortKey"
          [icon]="'assets/svg/swap-vertical-outline.svg'" />
      </form>
    </div>
    <div style="margin-bottom: 10px;">
      <tidy-small-button
        [text]="'Add Upgrade'"
        [image]="'assets/img/add-button-white.svg'"
        (buttonClick)="goToAddUpgradePage()" />
    </div>
  } @else {
    <!-- Mobile control mirrors — search pill, two filter pills, add button. -->
    <div style="margin-bottom: 10px;" class="sk-section sk-controls-search">
      <ion-skeleton-text [animated]="true" class="sk-search-pill" />
    </div>
    <div class="custom-desktop-header sk-section sk-controls-filters" style="margin-bottom: 10px;">
      <div class="form-filters" style="gap: 8px;">
        <ion-skeleton-text [animated]="true" class="sk-filter-pill" />
        <ion-skeleton-text [animated]="true" class="sk-filter-pill" />
      </div>
    </div>
    <div style="margin-bottom: 10px;" class="sk-section sk-controls-add">
      <ion-skeleton-text [animated]="true" class="sk-add-button" />
    </div>
  }
}

@if (!isDataLoaded) {
  @if (windowService.isDesktopRes) {
    <tidy-table
      [skeletonLoading]="true"
      [columnslabels]="tableHeaders" />
  } @else {
    <!-- Mirrors tidy-upgrade-card: icon + title/description header, the
         three-column stats strip (cost / impact pill / ROI bar) and the
         status button row. -->
    <div class="upgrades-grid">
      @for (i of [1, 2, 3, 4, 5, 6]; track i) {
        <tidy-card class="sk-upgrade-card sk-section">
          <div class="sk-card-header">
            <ion-skeleton-text [animated]="true" class="sk-card-icon" />
            <div class="sk-card-title">
              <ion-skeleton-text [animated]="true" class="sk-card-name" />
              <ion-skeleton-text [animated]="true" class="sk-card-description" />
            </div>
          </div>
          <div class="sk-card-stats">
            <div class="sk-card-stat">
              <ion-skeleton-text [animated]="true" class="sk-stat-label" />
              <ion-skeleton-text [animated]="true" class="sk-stat-value" />
            </div>
            <div class="sk-card-stat">
              <ion-skeleton-text [animated]="true" class="sk-stat-label" />
              <ion-skeleton-text [animated]="true" class="sk-impact-pill" />
            </div>
            <div class="sk-card-stat">
              <ion-skeleton-text [animated]="true" class="sk-stat-label" />
              <ion-skeleton-text [animated]="true" class="sk-stat-value" />
              <ion-skeleton-text [animated]="true" class="sk-roi-track" />
            </div>
          </div>
          <div class="sk-card-actions">
            <ion-skeleton-text [animated]="true" class="sk-status-button" />
            <ion-skeleton-text [animated]="true" class="sk-status-button" />
            <ion-skeleton-text [animated]="true" class="sk-status-button" />
          </div>
        </tidy-card>
      }
    </div>
  }
}

@if (isDataLoaded && upgrades.length) {
  @if (windowService.isDesktopRes) {
    <!-- The status buttons are part of the Status cell's HTML, so their clicks
         are delegated from the table host (see onStatusButtonClick). -->
    <tidy-table
      [dataSource]="tableRows"
      [displayedColumns]="tableHeaders"
      [columnslabels]="tableHeaders"
      [showColumnLines]="false"
      [rowsToShow]="50"
      [allRowIsClickable]="true"
      [skipEntrance]="true"
      [enableVirtualScrolling]="false"
      (sortChange)="onTableSortChange($event)"
      (cellClick)="onTableColumnClick($event)"
      (click)="onStatusButtonClick($event)"
      [infiniteScroll]="true"
      [totalRows]="tableRows.length" />
  } @else {
    <div class="upgrades-grid">
      @for (upgrade of upgrades; track upgrade.id) {
        <tidy-upgrade-card
          [upgrade]="upgrade"
          (edit)="onEditUpgrade($event)"
          (statusChange)="onStatusChange($event)" />
      }
    </div>
  }
}

@if (isDataLoaded && upgrades.length === 0 && rawUpgrades.length > 0) {
  <tidy-alert>
    <tidy-row>
      <tidy-text [body]="'No upgrades match your search.'" />
    </tidy-row>
  </tidy-alert>
}

@if (isDataLoaded && rawUpgrades.length === 0) {
  <tidy-alert>
    <tidy-row>
      <tidy-text [body]="'No upgrades configured. Add upgrades for your properties to track ideas, costs, and impact.'" />
    </tidy-row>
  </tidy-alert>
}
